/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState } from "react";
import { View, Image, ScrollView } from "react-native";
import { ic_videocam } from "react-icons-kit/md/ic_videocam";
import moment from "moment";

import { useSelector } from "react-redux";
import { RootState } from "store/types";

import { Alert, Text, UpduoButton, useInterval } from "UpduoLibrary";
import { SkillType, SummitType } from "graphql/internal/generated";
import summitPlaceholderImg from "assets/gradients/upduo-colors-04.svg";
import {
  skaleWhite,
  upduoPrimaryDark,
  upduoDarkGray,
  upduoPrimaryBlue,
} from "config/UpduoColors";
import { RecursivePartial } from "config/UtilityTypes";
import SummitStartCountdown from "./SummitStartCountdown";
import SummitTopicList from "./SummitTopicList";
import SummitDescription from "./SummitDescription";
import { getSummitAccentColor } from "./utils";
import {
  getNodes,
  getSummitState,
  SummitState,
  getResizedImageUrl,
} from "config/Utilities";
import TimeWindow from "./TimeWindow";
import SkillDescriptionModal from "./SkillDescriptionModal";
import * as UpduoAnalytics from "config/UpduoAnalytics";

function StartDateView({ date }: { date: Date }) {
  const [month, day] = moment(date).format("MMM D").split(" ");
  return (
    <View style={{ display: "flex", flexDirection: "row", marginBottom: 28 }}>
      <Text
        style={{
          color: upduoDarkGray,
          textAlign: "center",
          flexShrink: 1,
          fontSize: 16,
          lineHeight: "140%" as any,
          textTransform: "uppercase",
        }}
      >
        {month}
        {"\n"}
        {day}
      </Text>
    </View>
  );
}

export default function SummitPreview({
  summit,
  onConnect,
  connectButtonDisabled,
  connectButtonShowSpinner,
}: {
  summit: SummitType;
  onConnect: () => void;
  connectButtonDisabled?: boolean;
  connectButtonShowSpinner?: boolean;
}) {
  const selfObject = useSelector((state: RootState) => state.selfObject);

  const [now, setNow] = useState<Date>(new Date());
  const [skillDescriptionModalVisible, setSkillDescriptionModalVisible] =
    useState(false);
  const [selectedSkill, setSelectedSkill] =
    useState<RecursivePartial<SkillType>>();

  useInterval(() => {
    setNow(new Date());
  }, 1000);

  const startDate = new Date(summit?.startDate);
  const endDate = new Date(summit?.endDate);

  const summitState = getSummitState({ now, startDate, endDate });
  const summitColor = getSummitAccentColor(summit, summitState);
  const timeWindowList = getNodes(summit?.timeWindows);

  const rightColumnWidth = 322;

  return (
    <View
      style={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 12,
        shadowColor: "rgba(51, 60, 86, 0.25)",
        shadowRadius: 10,
        overflow: "hidden",
        height: "100%",
        backgroundColor: skaleWhite,
      }}
    >
      <View
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
          flexGrow: 1,
          flexBasis: 300,
        }}
      >
        <Image
          style={{ flexGrow: 1, flexShrink: 1, height: "100%" }}
          source={{
            uri: summit.imageUrl
              ? getResizedImageUrl(summit.imageUrl, 1024)
              : summitPlaceholderImg, // we don't know the size here, so use a reasonable max size
          }}
        />
        <View
          style={{
            width: rightColumnWidth,
          }}
        >
          {summitState === SummitState.ACTIVE && timeWindowList?.length > 0 ? (
            <TimeWindow timeWindowsList={timeWindowList} />
          ) : null}

          <ScrollView
            style={{
              paddingHorizontal: 24,
              paddingTop: 24,
              paddingBottom: 19.5,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <View
              style={{ flexDirection: "row", justifyContent: "space-between" }}
            >
              {summitState === SummitState.ACTIVE &&
              timeWindowList.length !== 0 ? null : (
                <StartDateView date={startDate} />
              )}

              <SummitStartCountdown
                startDate={startDate}
                endDate={endDate}
                now={now}
                color={summitColor}
              />
            </View>
            <Text
              style={{
                color: upduoPrimaryDark,
                fontSize: 20,
                lineHeight: "140%" as any,
              }}
            >
              {summit.name}
            </Text>
            <View
              style={{
                flexGrow: 1,
                marginTop: summitState === SummitState.ACTIVE ? 28 : 60,
              }}
            />
            <View>
              <UpduoButton
                style={{ backgroundColor: upduoPrimaryBlue }}
                disabled={
                  summitState !== SummitState.ACTIVE || connectButtonDisabled
                }
                loading={connectButtonShowSpinner}
                text={
                  summitState === SummitState.NOT_STARTED
                    ? "Event hasn't started"
                    : summitState === SummitState.COMPLETED
                    ? "Event has ended"
                    : "Connect"
                }
                onPress={() => {
                  if (selfObject.organizationHasTimeClockAlert) {
                    Alert(
                      "Are you currently clocked into your shift?",
                      "Please only use Upduo during working hours.",
                      [
                        {
                          text: "No",
                          onPress: () => {
                            UpduoAnalytics.logEvent(
                              "user-summit-clocked-in-alert",
                              {
                                isClockedIn: false,
                              }
                            );
                          },
                          style: "cancel",
                        },
                        {
                          text: "Yes",
                          onPress: () => {
                            UpduoAnalytics.logEvent(
                              "user-summit-clocked-in-alert",
                              {
                                isClockedIn: true,
                              }
                            );
                            onConnect();
                          },
                        },
                      ]
                    );
                  } else {
                    onConnect();
                  }
                }}
                icon={
                  summitState === SummitState.ACTIVE ? ic_videocam : undefined
                }
              />
            </View>
          </ScrollView>
        </View>
      </View>
      <View
        style={{
          height: 3,
          backgroundColor: summitColor,
          // shadowColor: "black",
          // shadowOpacity: 0.25,
          // shadowRadius: 12,
        }}
      />
      <div
        // this has to be a div so it can auto-hide scroll bars
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          overflow: "auto",
          flexShrink: 1,
          minHeight: 100,
          paddingTop: 18,
          boxShadow: "0 12px 12px -12px rgba(0, 0, 0, 0.25) inset",
        }}
      >
        <View
          style={{
            flexGrow: 1,
            height: "100%",
          }}
        >
          <SummitTopicList
            summit={summit}
            onSelectSkill={(skill: SkillType) => {
              setSelectedSkill(skill);
              setSkillDescriptionModalVisible(true);
            }}
          />
        </View>
        <View
          style={{
            width: rightColumnWidth,
            padding: 24,
          }}
        >
          <SummitDescription
            summit={summit}
            startDate={startDate}
            endDate={endDate}
          />
        </View>
      </div>
      <SkillDescriptionModal
        skillDescription={selectedSkill?.description || ""}
        isSkillPublished={selectedSkill?.isPublished || false}
        isVisible={skillDescriptionModalVisible}
        onClose={() => {
          setSkillDescriptionModalVisible(false);
        }}
        skillName={selectedSkill?.name || ""}
        communityName={summit?.organization?.name}
      />
    </View>
  );
}
